import { DirectUpload } from '@rails/activestorage';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelize } from 'src/utils/decamelize';
import { populateHasManyRelationshipFromRequest } from 'src/utils/populateRelationship';
import { isEmpty } from 'src/utils/typeCheck';

import { denormalize, normalize } from '../shared/normalizers';
import { User } from '../user/_types';
import { WorkoutPlan } from '../workout-plan/_types';

import {
  Equipment,
  Exercise,
  ExerciseDetail,
  ExerciseReviewsResponse,
  ExerciseSearchResult,
  PublicExerciseRelated,
  PublicExerciseStats,
} from './_types';
import {
  denormalizeExercise,
  normalizeExercise,
  normalizeExerciseSearchResult,
} from './normalizers';
interface SearchExercisesOptions {
  search: string;
  trainerId: Identifier;
  signal?: AbortSignal;
}

export const fetchExercises = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  summary,
  forUser = true,
  filters = {},
}: {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  summary?: boolean;
  forUser?: boolean | null;
  filters?: {
    isHeadTrainer?: boolean;
    trainerId?: string;
    muscleGroup?: number;
    difficultyLevel?: number;
    forceType?: string;
    tags?: string[];
    matchAllTags?: boolean;
    hasVideo?: number;
    status?: 'Archived';
    muscleGroups?: number[];
    difficulty?: number[];
    forceTypes?: string[];
    exerciseType?: number[];
    equipment?: number[];
    mechanicsType?: number[];
    sport?: number[];
  };
}) => {
  const response = await api.get(`/api/v3/exercises/`, {
    params: {
      page,
      per: perPage,
      for_user: forUser === null ? undefined : forUser,
      summary,
      ...(search && { 'q[name_cont]': search }),
      ...(sortBy && {
        'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
      }),
      ...(filters.trainerId &&
        !filters.isHeadTrainer && {
          'q[sub_trainer_id_eq]': filters.trainerId,
        }),
      ...(filters.isHeadTrainer && { 'q[sub_trainer_id_null]': 1 }),
      ...(filters.muscleGroup && {
        'q[primary_muscle_group_id_eq]': filters.muscleGroup,
      }),
      ...(!isEmpty(filters.difficultyLevel) && {
        'q[difficulty_level_id_eq]': filters.difficultyLevel,
      }),
      ...(filters.forceType === 'N/A' && {
        'q[no_force_type]': filters.forceType,
      }),
      ...(filters.forceType &&
        filters.forceType !== 'N/A' && {
          'q[force_type_eq]': filters.forceType,
        }),
      ...(filters.hasVideo === 1 && { 'q[video_url_present]': true }),
      ...(filters.hasVideo === 0 && { 'q[video_url_blank]': true }),
      // Without Match all tags
      ...(!filters.matchAllTags &&
        filters.tags && { 'q[tags_name_eq_any]': filters.tags }),
      // With Match all tags
      ...(filters.matchAllTags &&
        filters.tags && { 'q[exercise_tags]': filters.tags.join(',') }),
      ...(filters.status === 'Archived' && { archived_only: true }),
      ...(filters.muscleGroups && {
        'q[primary_muscle_group_id_in]': filters.muscleGroups,
      }),
      ...(filters.difficulty && {
        'q[difficulty_level_id_in]': filters.difficulty,
      }),
      ...(filters.forceType && {
        'q[force_type_id_in]': filters.forceType,
      }),
      ...(filters.exerciseType && {
        'q[exercise_types_id_in]': filters.exerciseType,
      }),
      ...(filters.equipment && {
        'q[equipment_id_in]': filters.equipment,
      }),
      ...(filters.mechanicsType && {
        'q[mechanics_type_id_in]': filters.mechanicsType,
      }),
      ...(filters.sport && {
        'q[sports_id_in]': filters.sport,
      }),
    },
  });

  const normalizedExercises = response.data?.exercise?.map((exercise) =>
    normalizeExercise(exercise)
  );

  return { exercises: normalizedExercises, total: response.data?.meta?.total };
};

export const fetchAllExercises = async (): Promise<Exercise[]> => {
  const response = await api.get('/api/v3/exercises', {
    params: {
      fetch_all: true,
      for_user: true,
      pluck_columns: 'id,name',
      'q[s]': 'name asc',
    },
  });

  return response.data.map(normalize);
};

export const searchExercises = async ({
  search,
  trainerId,
  signal,
}: SearchExercisesOptions): Promise<Exercise[]> => {
  const response = await api.get('/sm/search', {
    signal,
    params: {
      types: [`exercise_trainer_${trainerId}`],
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[`exercise_trainer_${trainerId}`].map(
    (r: ExerciseSearchResult) => normalizeExerciseSearchResult(r)
  );
};

interface GetExerciseOptions {
  exerciseId: Identifier;
}

export const fetchExerciseColumnsByIds = async <
  T extends readonly (keyof Exercise)[],
>(
  ids: Exercise['id'][],
  pluckColumns: T
) => {
  try {
    const response = await api.get(`/api/v3/exercises/`, {
      params: {
        with_ids: ids.join(','),
        pluck_columns: pluckColumns.join(','),
      },
    });
    return response.data.map((exercise) => normalize(exercise)) as Pick<
      Exercise,
      T[number]
    >[];
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error fetching exercise columns.'));
  }
};

export const getExercise = async ({
  exerciseId,
}: GetExerciseOptions): Promise<ExerciseDetail> => {
  const response = await api.get(`/api/v3/exercises/${exerciseId}`);
  const exercise = normalizeExercise(response.data) as ExerciseDetail;
  const alternateExercises = await populateHasManyRelationshipFromRequest({
    list: exercise.alternateExercises || [],
    idKey: 'id',
    newKey: 'alternateExercise',
    func: async (ids) => {
      const exerciseColumns = await fetchExerciseColumnsByIds(ids, [
        'id',
        'name',
      ]);
      return exerciseColumns.map((exerciseColumn) => {
        const alternateExercise = (exercise.alternateExercises || []).find(
          (alternateExercise) => alternateExercise.id === exerciseColumn.id
        );
        return {
          id: exerciseColumn.id,
          label: alternateExercise?.label,
          custom: alternateExercise?.custom,
          name: exerciseColumn.name,
        };
      });
    },
  });

  exercise.alternateExercises = alternateExercises.map(
    (exercise) => exercise.alternateExercise[0]
  );
  return exercise;
};

interface FetchExerciseOptions {
  id: Exercise['id'];
}

export const fetchExercise = async ({
  id,
}: FetchExerciseOptions): Promise<Exercise> => {
  const response = await api.get(`/api/v4/exercises/${id}`);
  return camelcaseKeys(response.data, { deep: true });
};

export const fetchPublicExerciseStats = async ({
  exerciseId,
}: {
  exerciseId: Identifier;
}): Promise<PublicExerciseStats> => {
  const response = await api.get(`/api/v2/exercises/${exerciseId}/public_stats`);
  return camelcaseKeys(response.data, { deep: true });
};

export const fetchPublicExerciseRelated = async ({
  exerciseId,
}: {
  exerciseId: Identifier;
}): Promise<PublicExerciseRelated> => {
  const response = await api.get(
    `/api/v2/exercises/${exerciseId}/public_related`
  );
  return camelcaseKeys(response.data, { deep: true });
};

export const fetchPublicExerciseReviews = async ({
  exerciseId,
}: {
  exerciseId: Identifier;
}): Promise<ExerciseReviewsResponse> => {
  const response = await api.get(
    `/api/v2/exercises/${exerciseId}/public_reviews`
  );
  return camelcaseKeys(response.data, { deep: true });
};

export const createPublicExerciseReview = async ({
  exerciseId,
  rating,
  content,
}: {
  exerciseId: Identifier;
  rating: number;
  content: string;
}) => {
  const response = await api.post(
    `/api/v2/exercises/${exerciseId}/create_public_review`,
    {
      comment: {
        rating,
        content,
      },
    }
  );
  return response.data;
};

export const updatePublicExerciseReview = async ({
  exerciseId,
  reviewId,
  rating,
  content,
}: {
  exerciseId: Identifier;
  reviewId: Identifier;
  rating: number;
  content: string;
}) => {
  const response = await api.put(
    `/api/v2/exercises/${exerciseId}/update_public_review`,
    {
      review_id: reviewId,
      comment: {
        rating,
        content,
      },
    }
  );
  return response.data;
};

interface UpdateExerciseOptions {
  id: Exercise['id'];
  data: Partial<Exercise>;
}

export const updateExercise = async ({
  id,
  data,
}: UpdateExerciseOptions): Promise<Exercise> => {
  const response = await api.patch(`/api/v4/exercises/${id}`, {
    exercise: denormalizeExercise(data as Partial<ExerciseDetail>),
  });
  return camelcaseKeys(response.data, { deep: true });
};

interface ThumbnailAsset {
  id: string;
  url: string;
  type: string;
}

export const createExerciseThumbnail = async (
  file: File,
  user: User
): Promise<ThumbnailAsset> => {
  try {
    const upload = new DirectUpload(
      file,
      '/api/v4/direct_uploads?service=google_public',
      {
        directUploadWillCreateBlobWithXHR: (xhr) => {
          xhr.setRequestHeader('API-TOKEN', user.authToken);
        },
      }
    );

    return new Promise<ThumbnailAsset>((resolve, reject) => {
      upload.create((error, blob) => {
        if (error) {
          reject(
            new Error(api.formatError(error, 'Failed to upload thumbnail'))
          );
        } else {
          if (!blob || !blob.signed_id) {
            reject(
              new Error('Invalid response from server: Missing blob data')
            );
            return;
          }

          const result: ThumbnailAsset = {
            id: blob.signed_id,
            url: `/rails/active_storage/blobs/redirect/${
              blob.signed_id
            }/${encodeURIComponent(file.name)}`,
            type: file.type,
          };
          resolve(result);
        }
      });
    });
  } catch (error: any) {
    throw new Error(
      api.formatError(error, 'Error creating exercise thumbnail.')
    );
  }
};

export const createAlternateExerciseThumbnail = async (
  file: File
): Promise<ThumbnailAsset> => {
  try {
    const formData = new FormData();
    formData.append('asset_image[image]', file);

    const response = await api.post('/api/v2/assets/image/exercise', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error: any) {
    throw new Error(
      api.formatError(error, 'Error creating exercise thumbnail.')
    );
  }
};

export const updateExerciseInfo = async ({
  exercise,
}: {
  exercise: Omit<Partial<ExerciseDetail>, 'id'> & Pick<ExerciseDetail, 'id'>;
}) => {
  const data = denormalizeExercise(exercise);
  try {
    const response = await api.put(
      `/api/v3/exercises/${exercise.id}`,
      { exercise: data },
      {}
    );
    return normalizeExercise(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error updating exercise.'));
  }
};

export const addExerciseInfo = async ({ exercise }) => {
  const data = denormalizeExercise(exercise);
  try {
    const response = await api.post(`/api/v3/exercises`, {
      exercise: data,
    });
    return normalizeExercise(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error adding exercise.'));
  }
};

interface BulkUpdateParams {
  exerciseIds: Exercise['id'][];
  muscleGroupId?: number | null;
  difficultyLevel?: number | null;
  measurements?: {
    reps: boolean;
    weight: boolean;
    time: boolean;
    distance: boolean;
  };
  streamAspectRatio?: string;
  archive?: number;
}

export const bulkUpdateExercises = async ({
  exerciseIds,
  muscleGroupId,
  difficultyLevel,
  measurements,
  streamAspectRatio,
  archive,
}: BulkUpdateParams) => {
  const sendMeasurements =
    measurements?.reps ||
    measurements?.time ||
    measurements?.weight ||
    measurements?.distance;

  try {
    const response = await api.get('/api/v2/exercises/bulk_update', {
      params: {
        exercise_ids: exerciseIds,
        ...(!archive && {
          primary_muscle_group_id: muscleGroupId,
          difficulty_level: difficultyLevel,
          measurements: sendMeasurements
            ? {
                reps: measurements.reps,
                weight: measurements.weight,
                time: measurements.time,
                distance: measurements.distance,
              }
            : null,
        }),
        ...(archive && { archive }),
        ...(streamAspectRatio && { stream_aspect_ratio: streamAspectRatio }),
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error bulk updating exercise.'));
  }
};

export const fetchExercisesVideoUrls = async () => {
  const response = await api.get('/api/v2/exercises', {
    params: {
      fetch_all: true,
      for_user: true,
      pluck_columns: 'id,video_url',
    },
  });
  return response.data.map((exercise) => normalize(exercise));
};

export const fetchExercisesWithIds = async (ids: Exercise['id'][]) => {
  const response = await api.get<Exercise[]>('/api/v2/exercises', {
    params: {
      with_ids: ids.join(','),
    },
  });
  return response.data.map((exercise) => normalize(exercise) as Exercise);
};

export const checkIfExerciseNameExists = async (
  id: string | undefined,
  name: string
) => {
  const response = await api.get(
    `/api/v2/exercises/check_name?id=${id}&name=${name}`
  );
  return response.data;
};

export const importExercises = async (data: any) => {
  const formData = new FormData();
  data.videos.forEach((video, index) => {
    if (video.swap) {
      formData.append(`videos[${index}][swap]`, video.swap);
    }
    if (video.tags) {
      video.tags.forEach((tag) => {
        formData.append(`videos[${index}][tags][]`, tag);
      });
    }
    if (video.types) {
      video.types.forEach((type) => {
        formData.append(`videos[${index}][types][]`, type);
      });
    }
    formData.append(`videos[${index}][url]`, video.url);
    formData.append(`videos[${index}][thumbnail]`, video.thumbnail);
    formData.append(`videos[${index}][title]`, video.title);
    formData.append(`videos[${index}][checked]`, 'true');
    formData.append(`videos[${index}][editing]`, 'false');
  });
  formData.append('distance_measurement', data.distanceMeasurement);
  formData.append('skip_duplicate', data.skipDuplicate);
  data.types.forEach((type) => {
    formData.append('types[]', type);
  });
  try {
    const response = await api.post(
      '/api/v2/exercises/import_exercises',
      formData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error importing exercises.'));
  }
};

export const fetchExercisesVideos = async ({
  type,
  page,
  itemsPerPage,
  searchValue,
  authToken,
}: {
  type: 'vimeo' | 'youtube';
  page?: number;
  itemsPerPage?: number;
  searchValue?: string;
  authToken?: string;
}) => {
  try {
    let response;
    if (type === 'vimeo') {
      response = await api.get('https://api.vimeo.com/me/videos', {
        headers: {
          'WEB-CLIENT': undefined,
          Authorization: `Bearer ${authToken}`,
        },
        params: {
          fields: 'pictures,link,name,manage_link',
          filter: 'embeddable',
          sort: 'alphabetical',
          direction: 'asc',
          page,
          per_page: itemsPerPage,
          ...(searchValue && { query: searchValue, weak_search: true }),
        },
      });
      return {
        ...response.data,
        data: response.data.data.map((video) => normalize(video)),
      };
    } else if (type === 'youtube') {
      response = await api.get(`/api/v3/users/get_videos`);
      if (Array.isArray(response.data)) {
        return response.data.map((video) => normalize(video));
      }
      return response.data;
    }
    return response;
  } catch (error: any) {
    const errMsg = error.response?.data?.error;
    throw {
      status: error.response?.status,
      message: errMsg || 'Error fetching exercises videos.',
    };
  }
};

export const fetchEquipments = async (): Promise<Equipment[]> => {
  try {
    const response = await api.get('/api/v3/equipment?fetch_all=true');
    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error fetching equipments.'));
  }
};

export const addEquipment = async (equipment: {
  equipment: {
    name: string;
    userId: Identifier;
  };
}) => {
  try {
    const response = await api.post(
      '/api/v3/equipment',
      denormalize(equipment)
    );
    return normalize(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Unable to add new equipment.'));
  }
};

export const fetchExercisePlans = async (
  exerciseId: Identifier
): Promise<WorkoutPlan[]> => {
  const response = await api.get(
    `/api/v2/exercises/plans?exercise_id=${exerciseId}`
  );
  return normalize(response.data);
};

export const fetchExerciseRoutines = async (
  exerciseId: Identifier
): Promise<WorkoutPlan[]> => {
  const response = await api.get(
    `/api/v2/exercises/routines?exercise_id=${exerciseId}`
  );
  return normalize(response.data);
};
